import React, { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';

/**
 * Shared date-picker field — shadcn Popover + Calendar combo. Replaces
 * the browser's native `<input type="date">` for visual consistency
 * across the app and gives leadership a one-click "Clear" affordance.
 *
 * Props:
 *  - value: ISO YYYY-MM-DD string (or empty string when cleared)
 *  - onChange: (isoString) => void — receives "" when cleared
 *  - testId: stable data-testid for the trigger button (and `-clear` suffix on the clear button)
 *  - placeholder: shown when value is empty
 *  - minDate / maxDate: optional ISO bounds — calendar disables anything outside
 *  - disabled: greys out the trigger
 *  - widthClass: trigger button width (default 'w-40')
 *  - align: PopoverContent alignment (default 'start')
 *  - anchorDate: ISO date of the paired range start — the calendar opens on
 *    its month (when this field is empty) and marks it with a teal ring so
 *    you scroll forward from the start date instead of "today".
 *  - quickRanges: with anchorDate set, shows a compact "+1m +3m +6m +12m"
 *    row that sets this field to anchor + N months − 1 day in one click.
 *  - quickWeeks: with anchorDate set, shows "+1w +2w +4w" chips instead —
 *    suited to short ranges like breaks/blackouts (anchor + N×7 − 1 days).
 */
export function DatePickerField({
  value,
  onChange,
  testId,
  placeholder = 'Pick a date',
  minDate,
  maxDate,
  disabled = false,
  widthClass = 'w-40',
  align = 'start',
  anchorDate,
  quickRanges = false,
  quickWeeks = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + 'T00:00:00Z') : undefined;
  const minD = minDate ? new Date(minDate + 'T00:00:00Z') : undefined;
  const maxD = maxDate ? new Date(maxDate + 'T00:00:00Z') : undefined;
  const anchorD = anchorDate ? new Date(anchorDate + 'T00:00:00Z') : undefined;
  let disabledMatcher;
  if (minD && maxD) disabledMatcher = [{ before: minD }, { after: maxD }];
  else if (minD) disabledMatcher = { before: minD };
  else if (maxD) disabledMatcher = { after: maxD };
  const fmtDisplay = (d) => d
    ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  // anchor + N months − 1 day (Date rollover handles short months)
  const addMonthsIso = (iso, n) => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1 + n, d));
    dt.setUTCDate(dt.getUTCDate() - 1);
    return dt.toISOString().slice(0, 10);
  };
  // anchor + N weeks − 1 day (an inclusive N-week window)
  const addWeeksIso = (iso, n) => {
    const dt = new Date(iso + 'T00:00:00Z');
    dt.setUTCDate(dt.getUTCDate() + n * 7 - 1);
    return dt.toISOString().slice(0, 10);
  };
  const quickChips = quickWeeks
    ? [1, 2, 4].map((n) => ({ label: `${n}w`, iso: anchorDate ? addWeeksIso(anchorDate, n) : null, title: `${n} week${n === 1 ? '' : 's'} from the start date` }))
    : [1, 3, 6, 12].map((n) => ({ label: `${n}m`, iso: anchorDate ? addMonthsIso(anchorDate, n) : null, title: `${n} month${n === 1 ? '' : 's'} from the start date` }));
  const showChips = (quickRanges || quickWeeks) && !!anchorDate;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          disabled={disabled}
          className={`h-8 ${widthClass} text-xs text-left px-2.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center justify-between gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
            value ? 'text-[#0F2040]' : 'text-slate-400'
          }`}
        >
          <span className="truncate">{value ? fmtDisplay(selected) : placeholder}</span>
          <CalendarIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected || anchorD}
          captionLayout="dropdown-buttons"
          fromYear={minD ? minD.getUTCFullYear() : new Date().getFullYear() - 10}
          toYear={maxD ? maxD.getUTCFullYear() : new Date().getFullYear() + 10}
          fixedWeeks
          modifiers={anchorD ? { anchor: anchorD } : undefined}
          modifiersClassNames={anchorD ? { anchor: 'ring-1 ring-inset ring-[#4ABFCF] rounded-md font-semibold text-[#0F2040]' } : undefined}
          onSelect={(d) => {
            if (!d) return;
            const iso = d.toISOString().slice(0, 10);
            onChange(iso);
            setOpen(false);
          }}
          disabled={disabledMatcher}
          initialFocus
        />
        {showChips || value ? (
          <div className="border-t border-slate-100 p-2 flex items-center justify-between gap-2">
            {showChips ? (
              <div className="flex items-center gap-1" data-testid={testId ? `${testId}-quick-ranges` : undefined}>
                <span className="text-[10px] text-slate-400 pr-0.5">Next</span>
                {quickChips.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => { onChange(c.iso); setOpen(false); }}
                    data-testid={testId ? `${testId}-quick-${c.label}` : undefined}
                    title={c.title}
                    className="text-[11px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 hover:border-[#4ABFCF] hover:text-[#0F2040] transition-colors"
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            ) : <span />}
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                data-testid={`${testId}-clear`}
                className="text-[11px] text-slate-500 hover:text-rose-600 px-2 py-1"
              >
                Clear
              </button>
            )}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export default DatePickerField;
